import styled, { FlattenSimpleInterpolation, css } from 'styled-components';
import {
  compose,
  margin,
  SpaceProps,
  opacity,
  OpacityProps,
  layout,
  LayoutProps,
  shadow,
  ShadowProps,
  position,
  PositionProps,
  grid,
  GridProps,
  flexbox,
  FlexboxProps,
  BackgroundColorProps,
  BorderColorProps,
  TextColorProps,
  system,
  ResponsiveValue,
} from 'styled-system';

/**
 * Popover placement options relative to its children.
 */
export type Placement = 'top' | 'right' | 'bottom' | 'left';

/**
 * Popover kind options
 */
export type PopoverKinds = 'filled' | 'outlined';

export type SystemProps = SpaceProps &
  BackgroundColorProps &
  BorderColorProps &
  ShadowProps &
  OpacityProps &
  LayoutProps &
  PositionProps &
  GridProps &
  TextColorProps &
  FlexboxProps;

export interface IContentProps extends SystemProps {
  /**
   * Placement of the popover relative to the children of `Popover`.
   */
  placement: Placement;

  /**
   *  The kind of popover to render. Can be either a filled or outlined popover
   *
   * * @default "outlined"
   */
  kind?: PopoverKinds;
}

export interface IContainerProps extends IContentProps, SystemProps {
  /**
   * Bounding rect dimensions of the child element. Used to calculate the
   * exact position of the Popover.
   */
  childrenDimensions: ClientRect;
}

/**
 * Popover style configuration.
 */
const config = {
  MARGIN: 15,
  WIDTH: 175,
  MAX_HEIGHT: 163,
  CARET_OFFSET: 13
};

/**
 * Calculates the CSS position values of the second part of the caret
 *  for the popover given its `placement`.
 *
 * @param placement Placement of the popover.
 * @param borderColor
 */
const getBottomCaretPosition = (
  placement: Placement,
  borderColor: string
): FlattenSimpleInterpolation => {
  switch (placement) {
    case 'top':
      return css`
        border-width: 7px;
        top: 100%;
        left: calc(50% - ${config.CARET_OFFSET}px);
        transform: translateX(50%);
        border-left-color: transparent;
        border-right-color: transparent;
        border-top-color: ${borderColor};
        border-bottom-color: transparent;
      `;
    case 'right':
      return css`
        border-width: 7px;
        top: calc(50% - ${config.CARET_OFFSET}px);
        left: calc(0% - ${config.CARET_OFFSET}px);
        transform: translateY(50%);
        border-left-color: transparent;
        border-right-color: ${borderColor};
        border-top-color: transparent;
        border-bottom-color: transparent;
      `;

    case 'left':
      return css`
        border-width: 7px;
        top: 50%;
        left: 100%;
        transform: translateY(-50%);
        border-left-color: ${borderColor};
        border-right-color: transparent;
        border-top-color: transparent;
        border-bottom-color: transparent;
      `;
    case 'bottom':
      return css`
        border-width: 7px;
        top: calc(0% - ${config.CARET_OFFSET}px - 1px);
        left: 50%;
        transform: translateX(-50%);
        border-left-color: transparent;
        border-right-color: transparent;
        border-top-color: transparent;
        border-bottom-color: ${borderColor};
      `;
    default:
      // No caret.
      return css`
        content: '';
      `;
  }
};

const getTopCaretPosition = (
  placement: Placement,
  borderColor: string
): FlattenSimpleInterpolation => {
  switch (placement) {
    case 'top':
      return css`
        border-width: 7px;
        top: calc(100% - 1px);
        left: calc(50% - ${config.CARET_OFFSET}px);
        transform: translateX(50%);
        border-left-color: transparent;
        border-right-color: transparent;
        border-top-color: ${borderColor};
        border-bottom-color: transparent;
      `;
    case 'right':
      return css`
        border-width: 7px;
        top: calc(50% - ${config.CARET_OFFSET}px);
        left: calc(0% - ${config.CARET_OFFSET}px + 1px);
        transform: translateY(50%);
        border-left-color: transparent;
        border-right-color: ${borderColor};
        border-top-color: transparent;
        border-bottom-color: transparent;
      `;

    case 'left':
      return css`
        border-width: 7px;
        top: 50%;
        left: calc(100% - 1px);
        transform: translateY(-50%);
        border-left-color: ${borderColor};
        border-right-color: transparent;
        border-top-color: transparent;
        border-bottom-color: transparent;
      `;
    case 'bottom':
      return css`
        border-width: 7px;
        top: calc(0% - ${config.CARET_OFFSET}px);
        left: 50%;
        transform: translateX(-50%);
        border-left-color: transparent;
        border-right-color: transparent;
        border-top-color: transparent;
        border-bottom-color: ${borderColor};
      `;
    default:
      // No caret.
      return css`
        content: '';
      `;
  }
};

/**
 * Calculates the CSS position values of the `container` element
 * relative to the given `childrenRef` based on the given `placement`.
 *
 * @param placement Placement of the popover.
 * @param childrenDimensions ClientRect of the popover children.
 */
const getPopoverPosition = (
  placement: Placement,
  childrenDimensions: ClientRect
): FlattenSimpleInterpolation | string => {
  switch (placement) {
    case 'top':
      return css`
        top: ${window.pageYOffset + childrenDimensions.top - config.MARGIN}px;
        left: ${childrenDimensions.left + childrenDimensions.width / 2}px;
        transform: translate(-50%, -100%);
        display: block;
      `;
    case 'right':
      return css`
        top: ${window.pageYOffset +
          childrenDimensions.top +
          childrenDimensions.height / 2}px;
        left: ${childrenDimensions.left +
          childrenDimensions.width +
          config.MARGIN}px;
        transform: translateY(-50%);
        display: block;
      `;

    case 'bottom':
      return css`
        top: ${window.pageYOffset +
          childrenDimensions.top +
          childrenDimensions.height +
          config.MARGIN}px;
        left: ${childrenDimensions.left + childrenDimensions.width / 2}px;
        transform: translateX(-50%);
        display: block;
      `;

    case 'left':
      return css`
        top: ${window.pageYOffset +
          childrenDimensions.top +
          childrenDimensions.height / 2}px;
        left: ${childrenDimensions.left - config.MARGIN}px;
        transform: translate(-100%, -50%);
        display: block;
      `;
    default:
      return '';
  }
};

/**
 * Will return a color from the theme given a string as is passed through
 * styled system props
 * @param color The color as a string, can be "primary.1", or a hex code
 * @param theme The theme object
 */
const getColorFromProp = (
  color:
    | ResponsiveValue<string | number | symbol>
    | ResponsiveValue<string>
    | undefined,
  theme: any
): string => {
  const [zero, one, two, three] = String(color).split('.');
  return (
    theme.colors[zero]?.[one]?.[two]?.[three] ||
    theme.colors[zero]?.[one]?.[two] ||
    theme.colors[zero]?.[one] ||
    String(color)
  );
};

/**
 * Will return a padding in pixels from the theme given a string as is passed through
 * styled system props
 * @param padding The padding as a string, number or theme value, can be "1px", {1}, {10}, etc
 * @param theme The theme object
 */
const getPaddingFromProp = (
  padding: ResponsiveValue<string | number | symbol>,
  theme: any
): string => {
  const paddingString = String(padding);

  // If padding string has px in it, then we're fine to just use this
  if (paddingString.includes('px')) return paddingString;

  // If padding is a number, check if its referring to a theme value
  if (typeof padding === 'number' && padding > theme.space.length)
    return paddingString.concat('px');

  // Return theme value if it is
  return theme.space[parseInt(paddingString, 10)].toString().concat('px');
};

/**
 * Returns the appropriate background color to render based on the kind of Popover
 *
 * @param kind The kind of popover to render
 * @param theme
 */
const getBgColorFromKind = (
  kind: PopoverKinds | undefined,
  theme: any
): string => {
  return kind === 'filled'
    ? theme.colors.state.productPrimary
    : theme.colors.grayScale[0];
};

// Create custom style function because backgroundColor import is broken:
// https://github.com/styled-system/styled-system/issues/1569
const backgroundColorStyleFn = system({
  backgroundColor: {
    property: 'backgroundColor',
    scale: 'colors'
  }
});

const borderColorStyleFn = system({
  borderColor: {
    property: 'borderColor',
    scale: 'colors'
  }
});

const textColorStyleFn = system({
  color: {
    property: 'color',
    scale: 'colors'
  }
});

/**
 * The popover container. Mainly used to calculate position.
 */
export const PopoverContainer = styled.div.attrs(() => ({
  onClick: (e: React.MouseEvent) => e.stopPropagation()
}))<IContainerProps>`
  position: absolute;

  /* Calculate CSS position for container based on children. */
  ${({ placement, childrenDimensions }) => {
    return getPopoverPosition(placement, childrenDimensions);
  }}

  &:before {
    content: '';
    position: absolute;
    z-index: -1;
    width: 0;
    height: 0;
    ${({ placement, borderColor, theme }) => {
      if (borderColor) {
        return getBottomCaretPosition(
          placement,
          getColorFromProp(borderColor, theme)
        );
      }
      return getBottomCaretPosition(
        placement,
        theme.colors.state.productPrimary
      );
    }}
    border-style: solid;
    transform-origin: 0 0;
  }

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    ${({ placement, backgroundColor, theme, kind }) => {
      if (backgroundColor) {
        return getTopCaretPosition(
          placement,
          getColorFromProp(backgroundColor, theme)
        );
      }
      return getTopCaretPosition(placement, getBgColorFromKind(kind, theme));
    }}
    border-style: solid;
    transform-origin: 0 0;
  }

  z-index: ${({ theme }) => theme.zIndices[4]};
  ${margin}
`;

/**
 * Container for content inside `Popover`. Renders border, background styles,
 * and caret.
 */
export const PopoverContent = styled.div<IContentProps>`
  ${({ theme, kind }) => `
    border-width: 1px;
    border-style: solid;
    border-radius: 4px;
    box-shadow: 0 2px 4px 1px rgba(0,0,0,0.2), 0 4px 8px 1px rgba(0,0,0,0.2);
    background-color: ${getBgColorFromKind(kind, theme)};
    border-color: ${theme.colors.state.productPrimary};
    ${kind === 'filled' ? 'color: white;' : ''}
    width: ${config.WIDTH}px;
    max-height: ${config.MAX_HEIGHT}px;
    overflow-y: auto;
    overflow-x: hidden;
  `}

  ${({ padding, p, paddingTop, paddingLeft, paddingRight, theme }) => {
    let pT = '0px';
    let pL = '0px';
    let pR = '0px';

    if (padding !== undefined) {
      pT = getPaddingFromProp(padding, theme);
      pL = getPaddingFromProp(padding, theme);
      pR = getPaddingFromProp(padding, theme);
    }
    if (p !== undefined) {
      pT = getPaddingFromProp(p, theme);
      pL = getPaddingFromProp(p, theme);
      pR = getPaddingFromProp(p, theme);
    }
    if (paddingTop !== undefined) {
      pT = getPaddingFromProp(paddingTop, theme);
    }
    if (paddingLeft !== undefined) {
      pL = getPaddingFromProp(paddingLeft, theme);
    }
    if (paddingRight !== undefined) {
      pR = getPaddingFromProp(paddingRight, theme);
    }
    return `
      padding-top: ${pT};
      padding-left: ${pL};
      padding-right: ${pR};
    `;
  }}

  &:after {
    content: '';
    display: block;
    ${({ theme, p, padding, paddingBottom }) => {
      if (padding !== undefined) {
        return `padding-bottom: ${getPaddingFromProp(padding, theme)};`;
      }
      if (p !== undefined) {
        return `padding-bottom: ${getPaddingFromProp(p, theme)};`;
      }
      if (paddingBottom !== undefined) {
        return `padding-bottom: ${getPaddingFromProp(paddingBottom, theme)};`;
      }
      return 'padding-bottom: 0px;';
    }}
  }

  ${compose(
    borderColorStyleFn,
    backgroundColorStyleFn,
    textColorStyleFn,
    shadow,
    layout,
    opacity,
    position,
    grid,
    flexbox
  )}
`;

/**
 * Container for children of `Popover`.
 */
export const ChildrenContainer = styled.div`
  display: inline-block;
  cursor: pointer;
`;
